'use client';

import React from "react";
import { useJigyasa } from "./state";

export const StageChip = ({ stage }) => {
  const { stages, stageColor } = useJigyasa();
  const s = stages.find((x) => x.key === stage);
  const cls = stageColor(stage);
  return (
    <span
      data-testid={`stage-chip-${stage}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {s?.label || stage}
    </span>
  );
};

// A pill switcher for the org's lines of business, with an inline "add a
// business" so running more than one is treated as normal and is easy to
// find — never a hidden advanced toggle. Shown even for single-line orgs
// (one pill + the add affordance), so the option is always discoverable.
export const LineSwitcher = () => {
  const { lines, activeLine, setActiveLine, addLine, myRole, enableLines } = useJigyasa();
  const [adding, setAdding] = React.useState(false);
  const [name, setName] = React.useState("");
  const canManage = ["owner", "admin"].includes(myRole) && enableLines !== false;
  const multi = lines && lines.length > 1;

  const confirmAdd = () => {
    const n = name.trim();
    if (!n) return;
    addLine(n);
    setName("");
    setAdding(false);
  };

  if (!lines || lines.length === 0) return null;
  // Solo (enableLines false) with a single board: hide the switcher entirely —
  // a one-person business should never be nudged to run multiple boards.
  if (enableLines === false && !multi) return null;

  return (
    <div data-testid="line-switcher" data-tour-id="line-switcher" className="mb-3">
      <div className="flex flex-wrap items-center gap-2">
        {lines.map((l) => (
          <button
            key={l.id}
            data-testid={`line-tab-${l.id}`}
            onClick={() => setActiveLine(l.id)}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
              activeLine.id === l.id
                ? "border-rose-600 bg-rose-600 text-white"
                : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {l.name}
          </button>
        ))}
        {canManage && (adding ? (
          <span className="inline-flex items-center gap-1.5">
            <input
              data-testid="add-line-input"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmAdd();
                if (e.key === "Escape") { setAdding(false); setName(""); }
              }}
              placeholder="e.g. Hardware shop"
              className="w-44 rounded-full border border-zinc-300 px-3 py-1.5 text-sm outline-none focus:border-zinc-900"
            />
            <button
              data-testid="add-line-confirm"
              onClick={confirmAdd}
              className="rounded-full bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
            >
              Add
            </button>
            <button
              data-testid="add-line-cancel"
              onClick={() => { setAdding(false); setName(""); }}
              className="rounded-full px-2 py-1.5 text-sm text-zinc-500 hover:text-zinc-800"
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            data-testid="add-line-btn"
            onClick={() => setAdding(true)}
            className="rounded-full border border-dashed border-zinc-400 px-3 py-1.5 text-sm font-medium text-zinc-600 transition-colors hover:border-zinc-900 hover:text-zinc-900"
          >
            + Add a business
          </button>
        ))}
      </div>
      {canManage && !multi && !adding && (
        <p data-testid="line-switcher-hint" className="mt-1.5 text-xs text-zinc-500">
          Run more than one business? Add each as its own board and switch between them here.
        </p>
      )}
    </div>
  );
};

export const OverdueBadge = ({ days }) => (
  <span
    data-testid="overdue-badge"
    className="inline-flex items-center rounded-full bg-red-700 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-white"
  >
    {days === 0 ? "Due today" : `${days}d late`}
  </span>
);

export const PrimaryButton = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex min-h-[44px] items-center justify-center rounded-lg bg-rose-600 px-4 font-medium text-white transition-colors hover:bg-rose-700 disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

export const SecondaryButton = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex min-h-[44px] items-center justify-center rounded-lg border border-zinc-300 bg-white px-4 font-medium text-zinc-800 transition-colors hover:bg-zinc-100 ${className}`}
  >
    {children}
  </button>
);

export const Card = ({ children, className = "", ...props }) => (
  <div
    {...props}
    className={`rounded-xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-5 ${className}`}
  >
    {children}
  </div>
);

export const SectionTitle = ({ children, badge, testId }) => (
  <div className="mb-3 flex items-center gap-2">
    <h2
      data-testid={testId}
      className="text-base font-semibold tracking-tight text-zinc-900 sm:text-lg"
    >
      {children}
    </h2>
    {badge}
  </div>
);

export const phoneIsValid = (raw) => {
  const cleaned = (raw || "").replace(/\D/g, "");
  const stripped = cleaned.replace(/^91/, "").replace(/^0+/, "");
  return stripped.length === 10;
};

export const normalizePhone = (raw) => {
  const cleaned = (raw || "").replace(/\D/g, "");
  return cleaned.replace(/^91/, "").replace(/^0+/, "");
};

// All human-facing dates render in Asia/Kolkata (Kreta build constraint).
export const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" });
};

// Just the time portion (for demo/visit appointments).
export const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" });
};

// "YYYY-MM-DD" for an <input type="date">, `days` from today (0 = today).
export const todayPlusISODate = (days = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// Build an ISO timestamp from a date-input value ("YYYY-MM-DD") and an
// optional time ("HH:MM"). No time → defaults to 9:00 local so the day is
// unambiguous across time zones.
export const buildNextDate = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  if (timeStr) {
    const [hh, mm] = timeStr.split(":").map(Number);
    dt.setHours(hh || 0, mm || 0, 0, 0);
  } else {
    dt.setHours(9, 0, 0, 0);
  }
  return dt.toISOString();
};
